import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import { trimText } from "../../utils/Functions";
import { Close, Delete, Download, MoreVert, Visibility } from "@mui/icons-material";
import FileIcon from "./FileIcon";
import { useTranslation } from "react-i18next";
import ActionRow from "../../features/admin/clinic/components/actions/ActionRow";
import { getFileExtension } from "../form/fileUpload/utils/FileUploadFunctions";
import { isFileViewable } from "./fileViewer/FileViewerUtils";

export default function FileDisplay({
  name,
  isLoading,
  canDelete,
  onView,
  onDownload,
  onDelete,
}) {
  const { t } = useTranslation();
  const fileExtension = getFileExtension(name);
  const isViewable = isFileViewable(name);

  return (
    <Grid item xs={6} sm={3} md={2} lg={2} p={2} sx={{ position: "relative" }}>
      {isLoading ? (
        <Stack m={5} justifyContent={"center"}>
          <LinearProgress sx={{ width: "100%" }} />
        </Stack>
      ) : (
        <Fragment>
          <Stack
            sx={{
              transition: "0.3s",
              "&:hover": {
                transform: "scale(1.20)",
              },
            }}
          >
            <Tooltip
              title={`${t(isViewable ? "View" : "Download")} ${String(name)}`}
            >
              <Link
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                onClick={onView}
              >
                <Stack
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  spacing={1}
                >
                  {<FileIcon fileExtension={fileExtension} />}
                  <Typography variant="body2" textAlign={"center"}>
                    {trimText(String(name), 15)}
                  </Typography>
                </Stack>
              </Link>
            </Tooltip>
          </Stack>

          <FileActionsDialog
            name={name}
            isViewable={isViewable}
            canDelete={canDelete}
            onView={onView}
            onDownload={onDownload}
            onDelete={onDelete}
          />
        </Fragment>
      )}
    </Grid>
  );
}

function FileActionsDialog({
  name,
  isViewable,
  canDelete,
  onView,
  onDownload,
  onDelete,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  function handleAction(action) {
    setOpen(false);
    action();
  }

  return (
    <Fragment>
      <Tooltip title={t("Actions")}>
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          data-testid="appointment.details.files.grid.actions-button"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              transform: "scale(1.05)",
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth={true}
        maxWidth={"xs"}
        data-testid="appointment.details.files.grid.actions-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          p={2}
        >
          <Stack minWidth={0}>
            <Typography variant="h6" component="h2" fontWeight={600}>
              {t("Actions")}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={name}>
              {name}
            </Typography>
          </Stack>
          <Tooltip title={t("Close")}>
            <IconButton
              onClick={() => setOpen(false)}
              color="error"
              data-testid="appointment.details.files.grid.actions-dialog.close-button"
            >
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider sx={{ opacity: 0.6 }} />

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ opacity: isViewable ? 1 : 0.5 }}>
            <ActionRow
              justButton
              icon={<Visibility />}
              label={t("View")}
              description={t(isViewable ? "ViewFileDesc" : "PreviewNotAvailable")}
              disabled={!isViewable}
              onClick={() => handleAction(onView)}
              testId="appointment.details.files.grid.view-button"
            />
          </Box>
          <Divider />
          <ActionRow
            justButton
            icon={<Download />}
            label={t("Download")}
            description={t("DownloadFileDesc")}
            onClick={() => handleAction(onDownload)}
            testId="appointment.details.files.grid.download-button"
          />
          {canDelete ? (
            <Fragment>
              <Divider />
              <ActionRow
                icon={<Delete />}
                label={t("Delete")}
                description={t("DeleteFileDesc")}
                color="error"
                onClick={() => handleAction(onDelete)}
                testId="appointment.details.files.grid.delete-button"
              />
            </Fragment>
          ) : null}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}
